// The org dashboard's API client.
//
// The request pipeline lives in `@vista-cloud/web-common` (vista#671) — it was
// hand-rolled here and again in the admin console, and the two had drifted in a
// way that mattered: this copy learned to omit `Content-Type` on a body-less
// request (vista#380) and the admin copy did not.
//
// What stays here is the one thing that is genuinely this app's: the
// stale-data banner.

import { createApiClient } from '@vista-cloud/web-common';
import { setStale } from '../store/staleStore.js';

// In dev the Vite proxy makes everything same-origin. In production the
// dashboard is on {slug}.vista-cloud.io and the API on api.vista-cloud.io, so
// VITE_API_BASE is set at build time and the client takes the cross-origin
// path (CF cookie forwarded as a header, credentials included).
const API_BASE = import.meta.env.VITE_API_BASE || window.location.origin;

/**
 * #786: update the global stale indicator on every successful response — set
 * when served stale-while-revalidate (agent unreachable → cached KV), cleared
 * on fresh data. Surfaced app-wide by `<StaleBanner>` in AppShell. Reading the
 * custom headers cross-origin requires the worker's
 * `Access-Control-Expose-Headers`.
 *
 * Returns a decorator so the flags land on the parsed body, which is what
 * consumers read; the client calls it before the body exists.
 */
function trackStaleness(res) {
  const stale = res.headers.get('X-Vista-Stale') === 'true';
  const staleSince = stale ? res.headers.get('X-Vista-Cached-At') : null;
  setStale(stale, staleSince);
  if (!stale) return undefined;
  return (data) => {
    if (data && typeof data === 'object') {
      data._stale = true;
      data._staleSince = staleSince;
    }
    return data;
  };
}

const client = createApiClient({ apiBase: API_BASE, onResponse: trackStaleness });

export const { api, setVistaToken, getVistaToken, getWsToken } = client;
